// apps/console/src/services/auth/index.ts
//
// Plan 4 Task 7 — Public surface for the SPA's auth client. At boot the
// factory picks one of two clients (cognito-client.ts from Task 3, or
// keycloak-client.ts from Tasks 5+6) at every call, based on the runtime
// /config.json `provider` value loaded via loadAuthConfig() (Task 2).
//
// loadAuthConfig() MUST be awaited in main.tsx BEFORE any component imports
// from this module — pick() reads getAuthConfig() which throws if the boot
// promise hasn't resolved.
//
// Every forwarding export is a `(...args) => pick().X(...args)` arrow so the
// provider is re-resolved on each call rather than captured at module load.
// This isn't strictly required today (provider can't change mid-session) but
// keeps the dispatch faithful to "factory dispatches at call time".
//
// The 30+ existing consumers of `services/auth` (useAuth.tsx, the 4 auth
// pages, profile MFA modals, etc.) route through here — flipping the
// provider in /config.json switches the SPA between Cognito and Keycloak at
// boot with zero code change.
//
// The factory dispatches each method to the right client, with one adapter:
// `signIn` normalizes Keycloak's `SignInResult` discriminated union into
// Cognito's existing throw-based contract (`Promise<string>` + throws
// `MfaChallengeError` / `NewPasswordRequiredError`) so the 30+ consumers
// don't fork per provider. The `IFrontendAuthClient` interface
// (`Promise<SignInResult>`) is the INTERNAL client contract; this factory is
// the boundary that adapts to the external (throw-based) contract that
// useAuth + Login.tsx already wire for.
//
// The 4 Cognito-specific error classes (MfaChallengeError,
// NewPasswordRequiredError, TenantDeletedError, TenantNotFoundError) are
// re-exported from ./cognito-client. useAuth + Login + ForgotPassword still
// import them; on Keycloak the factory's `signIn` adapter constructs the
// first two from the matching SignInResult shapes — Login.tsx's existing MFA
// + new-password screens work end-to-end on both providers without changes.

import * as cognito from './cognito-client'
import { MfaChallengeError, NewPasswordRequiredError } from './cognito-client'
import { getAuthConfig } from './config'
import * as keycloak from './keycloak-client'
import { PasswordUpdateRequiredError } from './password-update-required-error'
import { resolveValidAccessToken } from './valid-access-token'

type AuthClient = typeof cognito | typeof keycloak

function pick(): AuthClient {
  // Reads the memoized AuthConfig set by loadAuthConfig() at boot. Throws if
  // loadAuthConfig() hasn't resolved — which is why main.tsx awaits it before
  // mounting the React tree.
  const cfg = getAuthConfig()

  return cfg.provider === 'cognito' ? cognito : keycloak
}

// --- 11 IFrontendAuthClient methods, forwarded to the picked client --------
//
// The forwarding-arrow shape (`(...args) => pick().X(...args)`) is load-bearing.
// `pick()` MUST be re-evaluated on every call; capturing it at module load
// (`export const signIn = pick().signIn`) would freeze the provider before
// loadAuthConfig() resolves.
//
// `signIn` is the ONE adapter in this factory: it normalizes Keycloak's
// `SignInResult` discriminated union into Cognito's existing throw-based
// contract. Cognito's client throws `MfaChallengeError` /
// `NewPasswordRequiredError` natively; Keycloak's client returns a tagged
// union and the factory translates each tag to the matching throw shape.
// This keeps useAuth + Login.tsx — which already `try/catch (MfaChallengeError)`
// — provider-agnostic.

/**
 * signIn returns `Promise<string>` + throws `MfaChallengeError` /
 * `NewPasswordRequiredError` — the throw-based contract Cognito has always
 * used and useAuth + Login.tsx are wired for. On Cognito the underlying
 * client matches this shape natively; on Keycloak the client returns a
 * `SignInResult` discriminated union (`{kind: 'authenticated' |
 * 'mfa-required' | 'new-password-required'}`) and the factory adapts it
 * here.
 *
 * Without this adapter, on Keycloak `await authService.signIn()` returns a
 * `SignInResult` OBJECT at runtime while TypeScript (via the previous
 * `typeof cognito.signIn` type-erase) sees `string` — and the Login.tsx MFA
 * path that catches `MfaChallengeError` would never fire on Keycloak.
 */
export const signIn = async (email: string, password: string): Promise<string> => {
  const result = await pick().signIn(email, password)

  // Cognito client returns a raw access-token string directly.
  if (typeof result === 'string') return result

  // Keycloak client returns the SignInResult union — adapt each kind to the
  // throw-based shape that useAuth + Login already handle.
  if (result.kind === 'authenticated') return result.accessToken
  if (result.kind === 'mfa-required') {
    throw new MfaChallengeError(result.session, email)
  }

  if (result.kind === 'new-password-required') {
    throw new NewPasswordRequiredError(result.session, email)
  }

  if (result.kind === 'password-update-required') {
    throw new PasswordUpdateRequiredError(result.actionUrl, email)
  }

  // Exhaustiveness check — `result` is `never` here if all SignInResult kinds
  // are handled above. Adding a new kind without updating this branch
  // surfaces as a compile-time error.
  throw new Error(`Unhandled signIn result: ${JSON.stringify(result satisfies never)}`)
}

export const respondToMfaChallenge: typeof cognito.respondToMfaChallenge = (...args) =>
  pick().respondToMfaChallenge(...args)

/**
 * respondToNewPasswordChallenge — Cognito's signature is 3 args
 * (email, newPassword, session); Keycloak's adapter implements the 4-arg
 * `IFrontendAuthClient` signature (extra `userAttributes`) so it can `void`
 * the param before throwing ("NEW_PASSWORD_REQUIRED is a Cognito-only
 * flow"). The factory accepts the 4-arg interface shape and routes per
 * provider — Cognito gets 3 args, Keycloak gets 4 (and immediately throws
 * by design).
 */
export const respondToNewPasswordChallenge = async (
  email: string,
  newPassword: string,
  session: string,
  userAttributes: Record<string, string> = {},
): Promise<string> => {
  const client = pick()

  if (client === cognito) {
    return cognito.respondToNewPasswordChallenge(email, newPassword, session)
  }

  return keycloak.respondToNewPasswordChallenge(email, newPassword, session, userAttributes)
}

export const signOut: typeof cognito.signOut = (...args) => pick().signOut(...args)
export const refreshToken: typeof cognito.refreshToken = (...args) => pick().refreshToken(...args)

export const forgotPassword: typeof cognito.forgotPassword = (...args) => pick().forgotPassword(...args)
export const confirmForgotPassword: typeof cognito.confirmForgotPassword = (...args) =>
  pick().confirmForgotPassword(...args)
export const changePassword: typeof cognito.changePassword = (...args) => pick().changePassword(...args)

export const associateSoftwareToken: typeof cognito.associateSoftwareToken = (...args) =>
  pick().associateSoftwareToken(...args)
export const verifySoftwareToken: typeof cognito.verifySoftwareToken = (...args) => pick().verifySoftwareToken(...args)
export const setUserMFAPreference: typeof cognito.setUserMFAPreference = (...args) =>
  pick().setUserMFAPreference(...args)

// --- Token storage utilities (provider-independent) ------------------------
//
// Both clients persist tokens under the same localStorage keys
// (`gisify_access_token` / `gisify_refresh_token`), so getAccessToken() and
// clearStoredTokens() are provider-independent. Defined once here rather
// than re-exported from a specific client to make the provider-independence
// explicit at the surface — they keep working uniformly even if a future
// client adds its own persistence quirks alongside the shared keys.

const ACCESS_TOKEN_KEY = 'gisify_access_token'
const REFRESH_TOKEN_KEY = 'gisify_refresh_token'
// The id token + cached user that `useAuth` renders "signed in" from. They are NOT optional
// extras here: see clearStoredTokens().
const ID_TOKEN_KEY = 'gisify_token'
const USER_KEY = 'gisify_user'

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

/**
 * An access token that is actually still valid, refreshing it first if it is not.
 *
 * {@link getAccessToken} is a bare `localStorage` read with no notion of expiry, which is right for
 * callers that only ask whether a session exists. It is NOT right for the two surfaces that
 * authenticate directly against the LIVE BROKER — the helmet video stream and the SOS voice call —
 * which bypass `services/api/client.ts` and so inherit none of its 401-refresh-retry.
 *
 * The decision logic lives in `./valid-access-token` rather than here, and deliberately: reaching
 * it through this barrel drags `@aws-sdk/client-cognito-identity-provider` into any spec that
 * touches it, which crashes the vitest worker on CI (see `apps/console/CLAUDE.md`). This wiring is
 * the only untested part, and it is two references.
 */
export function getValidAccessToken(): Promise<string | null> {
  return resolveValidAccessToken({ read: getAccessToken, refresh: refreshToken })
}

/**
 * Clear the WHOLE session, not just the half that talks to the API.
 *
 * THIS USED TO REMOVE ONLY THE ACCESS AND REFRESH TOKENS, and leaving the other two behind is a
 * specific, recurring, and very confusing failure. `useAuth` decides "is somebody signed in" from
 * `gisify_token` + `gisify_user`, so a half-cleared session renders as a perfectly normal signed-in
 * console — nav bar, dashboards, everything — while nothing on the page can authenticate.
 *
 * DIAGNOSED 2026-08-10 on the X2 Teleop dashboard: every robot-feed tile showed an amber
 * `reconnecting` chip and "Connecting to camera…" forever. It looked exactly like five dead
 * cameras. The robot was fine and was encoding video the whole time; the browser had no access
 * token, no refresh token, and an id token that had expired three and a half days earlier.
 *
 * `useApiQuery` already clears all four on a 401 — so the two sign-out paths disagreed, and the
 * disagreement is the bug. One session, one clear.
 */
export function clearStoredTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(ID_TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

// --- Cognito-specific helpers re-exported for Login.tsx --------------------
//
// `getTenantIdFromHost` + `probeTenantStatus` are the pre-flight tenant-status
// probe Login.tsx runs on mount (Issues #259, #327A). They're SaaS-specific
// (probe the central `console.api.{domain}/tenants/status` endpoint), so the
// Keycloak runtime path won't reach them; but the import on Login.tsx still
// needs to resolve regardless of which provider is active.

export { getTenantIdFromHost, probeTenantStatus } from './cognito-client'

// --- 4 Cognito-specific error classes (re-exported from cognito-client) ----
//
// useAuth.tsx + Login.tsx + ForgotPassword.tsx still `instanceof` these. The
// Keycloak code paths don't construct them, but the imports still need to
// resolve regardless of which provider is active.

export { MfaChallengeError, NewPasswordRequiredError, TenantDeletedError, TenantNotFoundError } from './cognito-client'
export { PasswordUpdateRequiredError } from './password-update-required-error'

// --- Type re-exports -------------------------------------------------------

export type { IFrontendAuthClient, SignInResult } from './types'
export type { AuthConfig, CognitoConfig, KeycloakConfig } from './config'

// --- Config helpers (Task 8 consumers need direct provider access) ---------
//
// Register.tsx (Task 8) branches its UX on `getAuthConfig().provider` to
// render "self-registration disabled" on Keycloak vs the existing form on
// Cognito. Re-exporting both helpers from this single entrypoint keeps the
// Task 8 consumer's import surface aligned with the rest of the auth module.

export { getAuthConfig, loadAuthConfig } from './config'
